
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Package, Camera, Lightbulb, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { GearCategory, CAMERA_CATEGORIES, GE_CATEGORIES } from "@/types/gear";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

interface InventorySidebarProps {
  selectedCategory: string;
  onCategorySelect: (category: string) => void;
}

export function InventorySidebar({
  selectedCategory,
  onCategorySelect,
}: InventorySidebarProps) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);

  // Fetch category counts using the user-isolated function
  const { data: categoryCountsData } = useQuery({
    queryKey: ["category-counts", user?.id],
    queryFn: async () => {
      setIsLoading(true);
      
      if (!user) {
        console.log("No authenticated user found");
        return [];
      }

      console.log("🔍 SECURITY DEBUG - Fetching category counts for user:", user.id);

      // Use the RLS-protected function that only returns current user's data
      const { data, error } = await supabase.rpc('get_category_counts');

      if (error) {
        console.error("Error fetching category counts:", error);
        throw error;
      }
      
      console.log("✅ SECURITY DEBUG - Category counts received:", {
        totalCategories: data?.length || 0,
        currentUserId: user.id,
        counts: data
      });

      return data || [];
    },
    enabled: !!user,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true
  });

  // Process category counts from the secure function
  useEffect(() => {
    if (categoryCountsData) {
      const counts: Record<string, number> = {};
      let total = 0;
      
      // Initialize all categories with zero
      [...CAMERA_CATEGORIES, ...GE_CATEGORIES].forEach(cat => {
        counts[cat] = 0;
      });
      
      // Update counts from the secure function response
      categoryCountsData.forEach(item => {
        if (item.category) {
          counts[item.category] = Number(item.count) || 0;
          total += Number(item.count) || 0;
        }
      });
      
      console.log("🔍 SECURITY DEBUG - Processed category counts:", {
        totalItems: total,
        categoryCounts: counts,
        currentUserId: user?.id
      });
      
      setTotalCount(total);
      setCategoryCounts(counts);
      setIsLoading(false);
    }
  }, [categoryCountsData, user?.id]);

  // All items includes total count
  const allItems = {
    name: "All Items",
    category: "All",
    count: totalCount,
    icon: Package,
  };

  // Group categories by department
  const cameraDepartment = {
    name: "Camera Department",
    categories: CAMERA_CATEGORIES.map(category => ({
      name: category,
      count: categoryCounts[category] || 0,
    })),
  };

  const geDepartment = {
    name: "G&E Department",
    categories: GE_CATEGORIES.map(category => ({
      name: category,
      count: categoryCounts[category] || 0,
    })),
  };

  const handleCategoryClick = (category: string) => {
    onCategorySelect(category);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full bg-card/30" />
        <Skeleton className="h-6 w-5/6 bg-card/30" />
        <Skeleton className="h-6 w-4/6 bg-card/30" />
        <Skeleton className="h-6 w-5/6 bg-card/30" />
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-200px)]">
      <div className="space-y-4 pr-4">
        <div className="bg-card/30 backdrop-blur-sm rounded-lg border border-white/5 overflow-hidden">
          <Button
            variant={selectedCategory === "All" ? "secondary" : "ghost"}
            className={`flex items-center justify-between w-full py-5 px-4 text-left ${
              selectedCategory === "All" ? "bg-white/10" : ""
            }`}
            onClick={() => handleCategoryClick("All")}
          >
            <div className="flex items-center">
              <Package className="h-4 w-4 mr-2 text-white/60" />
              <span>{allItems.name}</span>
            </div>
            <span className="bg-white/10 py-0.5 px-2 rounded-md text-xs">
              {allItems.count}
            </span>
          </Button>
        </div>

        {/* Camera Department Section */}
        <div className="bg-card/30 backdrop-blur-sm rounded-lg border border-white/5 overflow-hidden">
          <div className="p-3 border-b border-white/5 flex items-center">
            <Camera className="h-4 w-4 mr-2 text-white/60" />
            <h3 className="text-sm font-medium text-white/90">Camera Department</h3>
          </div>
          <ScrollArea className="h-full max-h-[400px]">
            <div className="space-y-0">
              {cameraDepartment.categories.map((cat) => (
                <Button
                  key={cat.name}
                  variant={selectedCategory === cat.name ? "secondary" : "ghost"}
                  className={`flex items-center justify-between w-full py-2 px-4 text-left text-sm rounded-none ${
                    selectedCategory === cat.name ? "bg-white/10" : ""
                  }`}
                  onClick={() => handleCategoryClick(cat.name)}
                >
                  <span className="truncate">{cat.name}</span>
                  <span className="bg-white/10 py-0.5 px-2 rounded-md text-xs">
                    {cat.count}
                  </span>
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* G&E Department Section */}
        <div className="bg-card/30 backdrop-blur-sm rounded-lg border border-white/5 overflow-hidden">
          <div className="p-3 border-b border-white/5 flex items-center">
            <Lightbulb className="h-4 w-4 mr-2 text-white/60" />
            <h3 className="text-sm font-medium text-white/90">G&E Department</h3>
          </div>
          <ScrollArea className="h-full max-h-[400px]">
            <div className="space-y-0">
              {geDepartment.categories.map((cat) => (
                <Button
                  key={cat.name}
                  variant={selectedCategory === cat.name ? "secondary" : "ghost"}
                  className={`flex items-center justify-between w-full py-2 px-4 text-left text-sm rounded-none ${
                    selectedCategory === cat.name ? "bg-white/10" : ""
                  }`}
                  onClick={() => handleCategoryClick(cat.name)}
                >
                  <span className="truncate">{cat.name}</span>
                  <span className="bg-white/10 py-0.5 px-2 rounded-md text-xs">
                    {cat.count}
                  </span>
                </Button>
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </ScrollArea>
  );
}
